import React, { useEffect, useState } from "react";

import {
  Modal, Form, Radio, Select, Button,
  // Switch,
} from "antd";
import { useForm } from "antd/es/form/Form";
import { debounce } from "lodash";
import { toast } from "react-toastify";
import { v4 as uuid } from "uuid";

import CardStepper from "../../../Common/UIComponents/CardStepper";
import {
  getSearchableSelectProps,
  renderSelectPopupScrollbar,
  selectPopupClassNames,
} from "../../../Common/UIComponents/SelectPopupScrollbar";
import StyledScrollbar from "../../../Common/UIComponents/StyledScrollbar";
import StyledTooltip from "../../../Common/UIComponents/StyledTooltip";

import { getMandatoryUserData } from "../../../../utils/common";

import {
  getAreaLists, setSmartRandomizer, getSmartRandomizer, getEnvironmentFields,
} from "../../../../actions/envAnalytics";

import { ReactComponent as CloseIcon } from "../../../../assets/images/close.svg";
import { ReactComponent as LoadingDots } from "../../../../assets/images/common/LoadingDots.svg";
import { ReactComponent as CheckCircleIcon } from "../../../../assets/images/environment/CheckCircleIcon.svg";
import { ReactComponent as InfoIcon } from "../../../../assets/images/environment/InfoIcon.svg";

import "./RandomizerForm.css";

const FREQUENCY_RADIO_OPTIONS = [
  { label: "Weekly", value: "weekly" },
  { label: "BiWeekly", value: "bi_weekly" },
  { label: "Monthly", value: "monthly" },
  { label: "Quarterly", value: "quarterly" },
  { label: "Every 6 months", value: "half_yearly" },
  { label: "Annually", value: "annually" },
];

function AreasMultiSelect({
  value = [],
  onChange,
  onAreasChange,
  options,
  areaMap,
  loading,
  disabled,
  placeholder = "Select Areas",
}) {
  const selectedValues = value || [];

  const handleChange = (newValue) => {
    if (onChange) {
      onChange(newValue);
    }
    if (onAreasChange) {
      onAreasChange(newValue);
    }
  };

  const handleRemove = (id) => {
    handleChange(selectedValues.filter((item) => item !== id));
  };

  const getLabel = (id) => {
    const option = options.find((opt) => opt.value === id);
    return areaMap[id] || option?.label;
  };

  return (
    <div
      className="RandomizerForm__AreasField"
      style={{ "--areas-placeholder": `"${placeholder}"` }}
    >
      <Select
        mode="multiple"
        allowClear
        value={selectedValues}
        onChange={handleChange}
        options={options}
        placeholder=""
        {...getSearchableSelectProps()} // eslint-disable-line react/jsx-props-no-spreading
        rootClassName="RandomizerForm__SelectArea RandomizerForm__SelectArea--BelowTags"
        classNames={selectPopupClassNames()}
        virtual={false}
        listHeight={256}
        popupRender={(menu) => renderSelectPopupScrollbar(menu, {
          optionCount: (options || []).length,
        })}
        loading={loading}
        disabled={disabled}
        tagRender={() => null}
      />
      {selectedValues.length > 0 && (
        <div className="RandomizerForm__TagsContainer">
          {selectedValues.map((id) => {
            const label = getLabel(id);
            const isInvalid = !loading && !label;

            return (
              <span
                key={id}
                className={`RandomizerForm__Tag ${isInvalid ? "RandomizerForm__Tag--invalid" : ""}`}
                title={label || id}
              >
                <CheckCircleIcon className="RandomizerForm__TagCheck" aria-hidden />
                <span className="RandomizerForm__TagLabel">{label || id}</span>
                <button
                  type="button"
                  className="RandomizerForm__TagClose"
                  onClick={() => handleRemove(id)}
                  aria-label={`Remove ${label || id}`}
                >
                  <CloseIcon width={14} height={14} />
                </button>
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
}

const RandomizerForm = ({
  onCancel, onSubmit, setIsSwabLimitExceeded, setRandomizerPayload,
}) => {
  const [riskBased, setRiskBased] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [isRandomizerEnabled, setIsRandomizerEnabled] = useState(true);
  const [formInitialValues, setFormInitialValues] = useState({ max_swabs_pd: 1 }); // eslint-disable-line
  const [criticalAreasOptions, setCriticalAreasOptions] = useState([]);
  const [isUpdating, setIsUpdating] = useState(false); // eslint-disable-line
  const [isFormValid, setIsFormValid] = useState(false);
  const [areaMap, setAreaMap] = useState({}); // For mapping uuid → name
  const [selectedCriticalAreas, setSelectedCriticalAreas] = useState([]);
  const [criticalFields, setCriticalFields] = useState([]);
  const [criticalField, setCriticalField] = useState("");
  const [criticalFieldExists, setCriticalFieldExists] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  const [isAreasLoading, setIsAreasLoading] = useState(false);
  const [areAreaFieldsMapped, setAreAreaFieldsMapped] = useState(true);
  const [form] = useForm();

  const isAreasPending = isAreasLoading
    || (isUpdating && criticalFieldExists && !areAreaFieldsMapped);

  const hasEmptyPillInField = (fieldValues, options) => {
  // Build set of valid option values
    const validValues = new Set(options.map((opt) => opt.value));
    // Return true if any field value is not in validValues (empty pill)
    return fieldValues.some((val) => !validValues.has(val));
  };

  /**
   * Debounced function to validate required fields in the form.
   * Checks based on selected testing strategy (risk-based vs uniform).
   */
  const debouncedCheckFormValid = debounce(async () => {
    try {
      const values = await form.validateFields();
      const requiredFields = riskBased
        ? [
          "swab_testing_strategy",
          "high_risk_frequency",
          "low_risk_frequency",
          "critical_areas",
          "prioritize_non_critical",
          "critical_nearyby_areas",
          "high_traffic_areas",
          "max_swabs_pd",
          "schedule_strategy",
        ]
        : [
          "swab_testing_strategy",
          "uniform_frequency",
          "critical_areas",
          "critical_nearyby_areas",
          "high_traffic_areas",
          "max_swabs_pd",
          "schedule_strategy",
        ];

      const isValid = requiredFields.every((field) => {
        const value = values[field];
        if (Array.isArray(value)) return value.length > 0;
        return value !== undefined && value !== null && value !== "";
      });
      // Check if any field has empty pill
      const hasEmptyPills = hasEmptyPillInField(values.critical_areas || [], criticalAreasOptions)
      || hasEmptyPillInField(values.critical_nearyby_areas || [], criticalAreasOptions)
      || hasEmptyPillInField(values.high_traffic_areas || [], criticalAreasOptions);
      setIsFormValid(isValid && !hasEmptyPills);
    } catch {
      setIsFormValid(false);
    }
  }, 200);

  /**
   * Fetches dropdown options for critical areas from API and maps them to UUIDs.
    * Sets the options in the state for rendering in the form.
   */
  const getDropdownOptions = async () => {
    try {
      setIsAreasLoading(true);
      const [fieldsData, areaLists] = await Promise.all([getEnvironmentFields(), getAreaLists()]);
      const uuidMap = {}; // Store uuid -> area name
      if (!criticalField) {
        setAreaMap(uuidMap); // Save uuid mapping for later
        setCriticalAreasOptions([]);
        setCriticalFields(fieldsData.fields.filter((item) => fieldsData.link_pattern_fields.includes(item.json_field)));
        return;
      }
      const options = areaLists.area_lists[criticalField].map((val) => {
        const id = uuid();
        uuidMap[id] = val;
        return {
          label: val,
          value: id,
        };
      });
      setAreaMap(uuidMap); // Save uuid mapping for later
      setCriticalAreasOptions(options);
      setCriticalFieldExists(true);
      setCriticalFields(fieldsData.fields.filter((item) => fieldsData.link_pattern_fields.includes(item.json_field))); // Set the options for the Critical Field dropdown
    } catch (error) {
      toast.error("Something went wrong while fetching dropdown options.");
      setCriticalAreasOptions([]);
      onCancel();
    } finally {
      setIsAreasLoading(false);
    }
  };
  /**
   * Reverse maps area names to UUIDs using the areaMap state.
   * @param {*} names - Array of area names.
   * @returns
   */
  const mapNameToUuid = (names) => {
    const reversedMap = Object.fromEntries(Object.entries(areaMap).map(([uuids, name]) => [name, uuids]));
    return names?.map((name) => reversedMap[name]) || [];
  };

  /**
   * Initializes the form and sets up the initial values.
   *  @param {Object} values - The initial values for the form.
   */
  useEffect(() => {
    const getInitialValues = async () => {
      try {
        const {
          result, exists,
        } = await getSmartRandomizer();
        if (exists === false) {
          // If exists is false, it means "Randomizer settings not found"
          getDropdownOptions();
          return;
        }
        const [initialData] = result || {};

        if (initialData.enable_smart_randomizer !== undefined) {
          setIsRandomizerEnabled(initialData.enable_smart_randomizer);
        }

        if (initialData.swab_testing_strategy) {
          setRiskBased(initialData.swab_testing_strategy === "risk-based");
        }

        if (Object.keys(initialData).length > 0) {
          setIsUpdating(true);
          setAreAreaFieldsMapped(false);
          setFormInitialValues({ ...initialData });
          form.setFieldsValue({
            ...initialData,
            max_swabs_pd: initialData.max_swabs_pd,
          });
          setSelectedCriticalAreas(initialData.critical_areas);
        }

        if (initialData.randomizer_field) {
          setCriticalField(initialData.randomizer_field);
          setCriticalFieldExists(true);
        }
      } catch (error) {
        toast.error("Failed to load initial configuration");
        onCancel();
      } finally {
        setIsInitializing(false);
      }
    };
    getInitialValues();
  }, [] ); // eslint-disable-line
  /**
   * Effect to load critical area dropdown data on mount.
   */
  useEffect(() => {
    if (criticalField) {
      getDropdownOptions();
    }
  }, [criticalField]); // eslint-disable-line

  useEffect(() => {
    if (isInitializing || !Object.keys(areaMap).length) {
      return;
    }
    const formData = form.getFieldsValue();
    const updateAreas = {
      critical_areas: mapNameToUuid(formInitialValues.critical_areas),
      critical_nearyby_areas: mapNameToUuid(formInitialValues.critical_nearyby_areas),
      high_traffic_areas: mapNameToUuid(formInitialValues.high_traffic_areas),
    };
    form.setFieldsValue({
      ...formData,
      ...updateAreas,
    });
    // also update selectedCriticalAreas with UUIDs
    setSelectedCriticalAreas(updateAreas.critical_areas || []);
    setAreAreaFieldsMapped(true);
    debouncedCheckFormValid();
  }, [areaMap]); // eslint-disable-line

  /**
   * Maps UUIDs to area names using the areaMap state.
   * @param {string[]} uuids - Array of UUIDs.
   * @returns {string[]} Array of area names.
   */
  const mapUuidToName = (uuids) => uuids?.map((id) => areaMap[id]) || [];

  const handleFinish = async (values) => {
    setIsLoading(true);
    const formData = form.getFieldsValue();
    const { user_payload, company_name } = getMandatoryUserData();

    // Base payload with common fields
    const basePayload = {
      swab_testing_strategy: formData.swab_testing_strategy,
      critical_areas: mapUuidToName(formData.critical_areas),
      critical_nearyby_areas: mapUuidToName(formData.critical_nearyby_areas),
      high_traffic_areas: mapUuidToName(formData.high_traffic_areas),
      max_swabs_pd: formData.max_swabs_pd,
      schedule_strategy: formData.schedule_strategy,
      enable_smart_randomizer: isRandomizerEnabled,
      randomizer_field: formData.randomizer_field,
    };

    // Add specific fields based on the testing strategy
    const payload = riskBased
      ? {
        ...basePayload,
        high_risk_frequency: formData.high_risk_frequency,
        low_risk_frequency: formData.low_risk_frequency,
        prioritize_non_critical: formData.prioritize_non_critical,
      }
      : {
        ...basePayload,
        uniform_frequency: formData.uniform_frequency,
      };

    // Add mandatory fields to the payload
    payload.user_payload = user_payload;
    payload.company_name = company_name;

    try {
      // Call the setSmartRandomizer API
      const response = await setSmartRandomizer(payload);

      if (response.success) {
        toast.success("Smart Randomizer setup successfully!");
        form.resetFields();
        onSubmit(values);
        return;
      } if (response.message === "Daily limit exceed") {
        toast.error(response.message);
        setRandomizerPayload(payload);
        onCancel();
        setIsSwabLimitExceeded(true);
        return;
      }
      toast.error("An error occurred while setting up the Smart Randomizer.");
      debouncedCheckFormValid();
    } catch (error) {
      toast.error("An error occurred while setting up the Smart Randomizer.");
      debouncedCheckFormValid();
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal
      title=""
      open
      onCancel={onCancel}
      centered
      wrapClassName="RandomizerForm__Modal"
      footer={null}
      closable={false}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleFinish}
        onFieldsChange={debouncedCheckFormValid}
        validateTrigger={["onChange", "onBlur"]}
        className="RandomizerForm__Form"
        initialValues={formInitialValues}
        requiredMark={false}
      >
        {(isLoading || isInitializing) ? (
          <div className="RandomizerForm__LoadingContainer">
            <LoadingDots width={64} height={64} className="RandomizerForm__LoadingDots" />
            {isInitializing ? (<span className="RandomizerForm__LoadingText">Loading configuration...</span>) : (<span className="RandomizerForm__LoadingText">Generating priority scores and creating the perfect scheduling plan.</span>)}
          </div>
        ) : (
          <div className="RandomizerForm__Container">
            <StyledScrollbar autoHide className="RandomizerForm__Scrollbar">
              <div className="RandomizerForm__ScrollbarContent">
                <div className="RandomizerForm__Header">
                  <div className="RandomizerForm__Title-Header">
                    <div className="RandomizerForm-title-block" />
                    <span className="RandomizerForm-title">Smart Randomizer</span>
                  </div>
                  <span className="RandomizerForm__Header__Subtitle">
                    Answer a questionnaire to set up a smart randomizer plan. The randomizer will automate the scheduling of swab locations.
                  </span>
                  {/* <div className="RandomizerForm__Header__Switch">
                    <label className="RandomizerForm__QuestionText RandomizerForm__QuestionText--Switch">
                      Enable Smart randomizer
                      <StyledTooltip
                        title="Enable to automatically generate and refresh swab schedules based on risk."
                        placement="topLeft"
                        className="RandomizerForm__Tooltip"
                      >
                        <InfoIcon className="RandomizerForm__QuestionTooltip_Icon" />
                      </StyledTooltip>
                    </label>
                    <Switch
                      value={isRandomizerEnabled}
                      onChange={(value) => {
                        setIsRandomizerEnabled(value);
                        debouncedCheckFormValid();
                      }}
                      size="small"
                    />
                  </div> */}
                </div>
                <Form.Item
                  name="randomizer_field"
                  label={<span className="RandomizerForm__QuestionText">1. What is the critical field?</span>}
                  rules={[
                    {
                      required: true, message: "",
                    },
                  ]}
                >
                  <Select
                    allowClear={false}
                    options={criticalFields.map((field) => ({ label: field.title_field, value: field.json_field }))}
                    placeholder="Select critical field"
                    {...getSearchableSelectProps()} // eslint-disable-line react/jsx-props-no-spreading
                    rootClassName="RandomizerForm__SelectArea RandomizerForm__SelectArea--CriticalField"
                    classNames={selectPopupClassNames()}
                    virtual={false}
                    listHeight={256}
                    popupRender={(menu) => renderSelectPopupScrollbar(menu, {
                      optionCount: (criticalFields || []).length,
                    })}
                    disabled={criticalFieldExists && isUpdating}
                    onChange={(value) => setCriticalField(value)}
                  />
                </Form.Item>
                <label className="RandomizerForm__QuestionText">2. Choose Testing Strategy</label>
                <Form.Item
                  name="swab_testing_strategy"
                  label={(
                    <span className="RandomizerForm__QuestionText">
                      a. How do you want to schedule your swabs?
                    </span>
              )}
                  width="100%"
                  required
                  rules={[{ required: true, message: "" }]}
                >
                  <Radio.Group
                    className="RandomizerForm__Radio"
                    onChange={(e) => {
                      setRiskBased(e.target.value !== "uniform");
                    }}
                    options={[
                      { label: "Risk Based Testing", value: "risk-based" },
                      { label: "Uniform Testing", value: "uniform" },
                    ]}
                  />
                </Form.Item>
                <label className="RandomizerForm__QuestionText">b. How often should swabs be conducted for different risk levels?</label>
                {riskBased && (
                <>
                  <Form.Item
                    name="high_risk_frequency"
                    label={(
                      <span className="RandomizerForm__QuestionText_1">
                        High-Risk Zones
                        <StyledTooltip
                          title="High-Risk Zone: Areas with a contamination history in the last 5 records, designated critical areas, high-traffic areas, and non-critical areas that tested out of spec are usually considered high risk."
                          placement="topLeft"
                          className="RandomizerForm__Tooltip RandomizerForm__Tooltip--Zone"
                          rules={[{ required: true, message: "" }]}
                        >
                          <InfoIcon className="RandomizerForm__QuestionTooltip_Icon" />
                        </StyledTooltip>
                      </span>
                  )}
                    width="100%"
                    required
                    className="RandomizerForm__Question_container RandomizerForm__Question_container--highrisk"
                  >
                    <Radio.Group
                      className="RandomizerForm__Radio"
                      options={FREQUENCY_RADIO_OPTIONS}
                    />
                  </Form.Item>
                  <Form.Item
                    name="low_risk_frequency"
                    label={(
                      <span className="RandomizerForm__QuestionText_1">
                        Low-Risk Zones
                        <StyledTooltip
                          title="Low-Risk Zone: Areas with minimal contamination history, low traffic, and no recent out-of-spec results are usually considered low risk zone."
                          placement="topLeft"
                          className="RandomizerForm__Tooltip RandomizerForm__Tooltip--Zone"
                          rules={[{ required: true, message: "" }]}
                        >
                          <InfoIcon className="RandomizerForm__QuestionTooltip_Icon" />
                        </StyledTooltip>
                      </span>
                  )}
                    width="100%"
                    required
                    className="RandomizerForm__Question_container"
                  >
                    <Radio.Group
                      className="RandomizerForm__Radio"
                      options={FREQUENCY_RADIO_OPTIONS}
                    />
                  </Form.Item>
                </>
                )}
                { !riskBased && (
                <Form.Item
                  name="uniform_frequency"
                  width="100%"
                  required
                  // className="RandomizerForm__Question_container"
                  rules={[{ required: true, message: "" }]}
                >
                  <Radio.Group
                    className="RandomizerForm__Radio"
                    options={FREQUENCY_RADIO_OPTIONS}
                  />
                </Form.Item>
                )}
                <Form.Item
                  name="critical_areas"
                  label={<span className="RandomizerForm__QuestionText">3. What are the critical areas?</span>}
                  // help={!criticalFieldExists && "Please select a critical field first"}
                  rules={[
                    {
                      required: true, message: "",
                    },
                  ]}
                >
                  <AreasMultiSelect
                    options={criticalAreasOptions.map((option) => ({ ...option, disabled: option.isInvalid }))}
                    areaMap={areaMap}
                    loading={isAreasPending}
                    disabled={!criticalFieldExists}
                    onAreasChange={(selectedValues) => {
                      const nearSelectedAreas = form.getFieldValue("critical_nearyby_areas") || [];
                      const result = nearSelectedAreas.filter((item) => !selectedValues.includes(item));
                      form.setFieldsValue({ critical_nearyby_areas: result });
                      setSelectedCriticalAreas(selectedValues);
                    }}
                  />
                </Form.Item>
                {riskBased && (
                <>
                  <Form.Item
                    name="prioritize_non_critical"
                    label={(
                      <span className="RandomizerForm__QuestionText">
                        4. Prioritize non-critical areas if contamination is detected?
                      </span>
                  )}
                    width="100%"
                    required
                    rules={[{ required: true, message: "" }]}
                  >
                    <Radio.Group
                      className="RandomizerForm__Radio"
                      options={[
                        { label: "Yes", value: true },
                        { label: "No", value: false },
                      ]}
                    />
                  </Form.Item>
                </>
                )}
                <Form.Item
                  name="critical_nearyby_areas"
                  label={(
                    <span className="RandomizerForm__QuestionText">
                      {riskBased ? "5. " : "4. "}
                      Which areas are near critical operations areas?
                    </span>
                )}
                  rules={[
                    {
                      required: true, message: "",
                    },
                  ]}
                >
                  <AreasMultiSelect
                    options={criticalAreasOptions.map((option) => ({ ...option, disabled: selectedCriticalAreas?.includes(option.value) || option.isInvalid }))}
                    areaMap={areaMap}
                    loading={isAreasPending}
                    disabled={!criticalFieldExists}
                  />
                </Form.Item>
                <Form.Item
                  name="high_traffic_areas"
                  label={(
                    <span className="RandomizerForm__QuestionText">
                      {riskBased ? "6. " : "5. "}
                      {" "}
                      Which areas have high traffic or frequent use?
                    </span>
                )}
                  rules={[
                    {
                      required: true, message: "",
                    },
                  ]}
                >
                  <AreasMultiSelect
                    options={criticalAreasOptions.map((option) => ({ ...option, disabled: option.isInvalid }))}
                    areaMap={areaMap}
                    loading={isAreasPending}
                    disabled={!criticalFieldExists}
                  />
                </Form.Item>
                <Form.Item
                  name="max_swabs_pd"
                  label={(
                    <span className="RandomizerForm__QuestionText">
                      {riskBased ? "7. " : "6. "}
                      What is the maximum number of swabs allowed per day?
                    </span>
                    )}
                  width="100%"
                  required
                >
                  <CardStepper
                    min={1}
                    max={100}
                    onChange={(value) => {
                      form.setFieldsValue({ max_swabs_pd: value });
                    }}
                  />
                </Form.Item>
                <Form.Item
                  name="schedule_strategy"
                  label={(
                    <span className="RandomizerForm__QuestionText">
                      {riskBased ? "8. " : "7. "}
                      Should swabs be scheduled only on business days, or include weekends as well?
                    </span>
                )}
                  width="100%"
                  required
                  rules={[{ required: true, message: "" }]}
                >
                  <Radio.Group
                    className="RandomizerForm__Radio"
                    options={[
                      { label: "Full Week", value: "full_week" },
                      { label: "Business Days", value: "business_days" },
                    ]}
                  />
                </Form.Item>
              </div>
            </StyledScrollbar>
            <div className="RandomizerForm__ButtonContainer">
              <Button
                htmlType="button"
                type="default"
                onClick={onCancel}
              >
                Cancel
              </Button>
              <Button
                htmlType="button"
                type="primary"
                onClick={handleFinish}
                disabled={!isFormValid}
              >
                {isUpdating ? "Apply" : "Done"}
              </Button>
            </div>
          </div>
        )}
      </Form>
    </Modal>
  );
};

export default RandomizerForm;
